// Site-wide policy / informational pages. All copy is intentionally formal,
// compliance-friendly, and clearly positions MDM Sahayak as an INDEPENDENT
// digital productivity / documentation-assistance platform — privately
// operated, with no claim of affiliation to any public administration,
// regulatory body, school board or programme authority.
//
// Pages exported: AboutUs, PrivacyPolicy, TermsConditions, ContactUs,
// RefundPolicy. All five share the Shell wrapper (back-link + eyebrow +
// title + last-updated). About + Contact additionally render the
// independence Disclaimer banner above their content.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Target, Eye, Clock, ShieldCheck, FileText,
  CheckCircle2, Mail, Phone, MessageCircle, MapPin, Briefcase,
  Layers, Zap, BookOpen, AlertCircle, Send,
} from "lucide-react";
import { toast } from "sonner";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const LAST_UPDATED = "February 2026";

function Shell({ title, eyebrow, lastUpdated, showDisclaimer = false, children }) {
  return (
    <main
      className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12"
      data-testid={`policy-shell-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-clay hover:text-clay-dark"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to MDM Sahayak
      </Link>
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-clay">{eyebrow}</p>
      <h1 className="mt-2 font-heading text-4xl font-semibold tracking-tight text-ink-900 sm:text-5xl">{title}</h1>
      {lastUpdated && (
        <p className="mt-2 text-xs text-ink-700">Last updated · {lastUpdated}</p>
      )}
      {showDisclaimer && <div className="mt-6"><DisclaimerBanner /></div>}
      <div className="prose prose-sand mt-8 max-w-none text-ink-900">{children}</div>
    </main>
  );
}

// =================================================================
// ABOUT US
// =================================================================
export function AboutUs() {
  const benefits = [
    { icon: Clock, title: "Save Significant Time", body: "Replace hours of manual paperwork — voucher prep, calculation sheets, hand receipts, utilization certificates — with a few clicks." },
    { icon: Zap, title: "Reduce Repetitive Workload", body: "Auto-fill structured formats from a single daily entry; remove duplicate data-typing across registers." },
    { icon: BookOpen, title: "Focus on Teaching", body: "Spend less time on documentation, more time on classrooms and learners — the work that matters most." },
    { icon: Layers, title: "Organised Workflows", body: "All your records in one place: daily entries, monthly summaries, quarterly statements, receipts, vendors, and CCH details." },
    { icon: ShieldCheck, title: "Reasonable Security Measures", body: "JWT-based authentication, tenant-isolated data, and role-based access controls protect your account." },
    { icon: Sparkles, title: "Mobile-friendly Experience", body: "Responsive design across phones, tablets and desktops — work from anywhere, including the staff room or your kitchen." },
  ];
  return (
    <Shell title="About MDM Sahayak" eyebrow="About" lastUpdated={LAST_UPDATED} showDisclaimer>
      <p>
        <b>MDM Sahayak</b> is an independently developed Software-as-a-Service (SaaS) productivity
        and workflow assistance platform. The platform is designed to help users — including
        teachers, schools, and administrative staff — simplify repetitive documentation and
        reporting tasks they routinely perform in their day-to-day work.
      </p>
      <p>
        Many institutional users spend significant time manually preparing the same types of
        documents over and over again — calculation sheets, voucher formats, hand receipts,
        utilization certificate templates, monthly summaries, and related reporting paperwork.
        MDM Sahayak provides digital utility and workflow assistance tools that help generate
        properly structured documentation formats for productivity and convenience purposes.
      </p>
      <p>
        The platform is intended purely as an independent digital convenience tool. It is{" "}
        <b>not</b> an official portal of any public body. It is <b>not</b> an official
        programme system. It is <b>not</b> an authorised public-administration website.
        MDM Sahayak operates independently, is not affiliated with or endorsed by any
        public administration, regulatory body, school board, or programme authority, and
        provides only digital utility and workflow assistance tools.
      </p>

      {/* Mission / Vision cards */}
      <div className="not-prose mt-10 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-clay-dark">
            <Target className="h-4 w-4" />
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Our Mission</p>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ink-700">
            To make routine administrative documentation effortless — so educators and staff
            can spend their time and energy on teaching, learning, and student care rather
            than on repetitive paperwork.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-clay-dark">
            <Eye className="h-4 w-4" />
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Our Vision</p>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ink-700">
            A future where every educator has access to clean, mobile-friendly digital tools
            that quietly handle the boring parts of administration — leaving humans free to
            teach, mentor, and lead.
          </p>
        </div>
      </div>

      {/* Benefits grid */}
      <h2 className="mt-12">Why teams choose MDM Sahayak</h2>
      <div className="not-prose mt-2 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {benefits.map((b) => (
          <div key={b.title} className="rounded-2xl border border-border bg-white p-4 shadow-sm">
            <b.icon className="h-5 w-5 text-clay" />
            <p className="mt-2 font-heading text-base font-semibold text-ink-900">{b.title}</p>
            <p className="mt-1 text-xs leading-relaxed text-ink-700">{b.body}</p>
          </div>
        ))}
      </div>

      {/* Productivity & time saving focus */}
      <h2 className="mt-12">Productivity &amp; Time-Saving Focus</h2>
      <p>
        Every feature in MDM Sahayak is shaped by one simple goal: reduce the number of minutes
        a user spends on routine paperwork each week. Daily entries become monthly summaries
        automatically; monthly data flows into quarterly statements without re-keying;
        previously-typed school, vendor, and cook details flow into every receipt or voucher
        that asks for them.
      </p>

      <h2>How we describe ourselves</h2>
      <ul>
        <li><b>Digital productivity platform</b> for repetitive administrative work.</li>
        <li><b>Documentation assistance platform</b> for structured form-based output.</li>
        <li><b>Workflow assistance tool</b> for daily / monthly / quarterly reporting cadences.</li>
        <li><b>Independent SaaS platform</b> — privately operated; not affiliated with any public body.</li>
        <li><b>Digital utility tool</b> for users who currently maintain similar records manually.</li>
      </ul>

      <h2>Independent Platform Clarification</h2>
      <p>
        MDM Sahayak is independently operated. It is not an official portal, not an
        authorised public-administration system, and not affiliated with any school board,
        regulatory body or programme authority. The platform only provides digital utility
        and workflow assistance tools. Users remain responsible for independently verifying
        and submitting any official records through the appropriate official channels.
      </p>
    </Shell>
  );
}

// =================================================================
// PRIVACY POLICY
// =================================================================
export function PrivacyPolicy() {
  return (
    <Shell title="Privacy Policy" eyebrow="Legal" lastUpdated={LAST_UPDATED}>
      <p>
        This Privacy Policy explains how MDM Sahayak (referred to as "we", "us", "our", or
        "the platform") collects, uses, and safeguards information when you use the Service.
        MDM Sahayak operates as an independent privately-operated software platform; it is
        not affiliated with any public administration, regulatory body, school board, or
        programme authority.
      </p>

      <h2>1. Information Collected</h2>
      <p>We may collect the following categories of information when you create an account or use the platform:</p>
      <ul>
        <li><b>Account information</b> — your name, mobile number, email (if provided), institution name, and other identifiers you choose to register with.</li>
        <li><b>Workspace data</b> — the documentation content you create within the platform (entries, vendor details, cook details, attendance figures, and similar records you enter).</li>
        <li><b>Usage information</b> — pages visited, actions taken (clicks, form submissions), and timestamps, used to keep the platform stable and to improve features.</li>
        <li><b>Device &amp; browser data</b> — device type, operating system, browser version, and IP address (for security &amp; abuse-prevention).</li>
      </ul>

      <h2>2. User Data Usage</h2>
      <p>We use the information described above to:</p>
      <ul>
        <li>Provide, maintain, and improve the platform's productivity and documentation features.</li>
        <li>Authenticate you and keep your workspace data secure and tenant-isolated.</li>
        <li>Respond to support requests and notify you about meaningful service events.</li>
        <li>Detect and prevent fraud, abuse, or unauthorised access.</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>
      <p>
        <b>We do not sell your data.</b> We do not rent or trade your personal information to
        third parties for advertising purposes.
      </p>

      <h2>3. Login Information</h2>
      <p>
        Account credentials are stored using industry-standard hashing techniques. We never
        store your password in plain text. You are responsible for keeping your login
        credentials confidential and for any activity occurring under your account.
      </p>

      <h2>4. Device / Browser Data</h2>
      <p>
        We log basic device and browser metadata along with your IP address for the limited
        purposes of security, rate-limiting, and abuse-prevention. This data is retained only
        as long as necessary for those operational purposes.
      </p>

      <h2>5. Cookies</h2>
      <p>
        We use a small number of essential cookies / browser storage entries to keep you
        signed in, remember your preferences, and operate core platform features. We do not
        place advertising cookies. You may disable cookies in your browser settings, but
        certain features (such as staying signed in) will not work without them.
      </p>

      <h2>6. Data Protection</h2>
      <p>
        We apply reasonable security measures to protect your data, including encrypted
        transport (HTTPS), authentication tokens, tenant isolation between accounts, and
        role-based access controls. No system can be guaranteed 100% secure, and you
        acknowledge that you provide your data at your own risk.
      </p>

      <h2>7. Third-Party Services</h2>
      <p>
        The platform may use carefully selected third-party services (for example, payment
        gateways such as Razorpay, or cloud hosting providers) to operate. These third parties
        process data only as needed to provide their respective services and are bound by
        their own privacy policies. We do not control and are not responsible for the
        independent practices of those services beyond our integration with them.
      </p>

      <h2>8. User Responsibilities</h2>
      <ul>
        <li>Provide accurate information when creating your account or entering records.</li>
        <li>Keep your password and login credentials confidential.</li>
        <li>Do not upload sensitive personal information of third parties without lawful basis.</li>
        <li>Use the platform only for its intended productivity / workflow purposes.</li>
      </ul>

      <h2>9. Children</h2>
      <p>
        The platform is intended for adult users (teachers, staff, administrators). It is not
        directed at children. If you believe a child has provided personal information,
        please contact us so we can remove it.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we will update the
        "Last updated" date at the top of this page. Material changes will also be announced
        within the platform or via email where applicable.
      </p>

      <h2>11. Contact Information</h2>
      <p>
        For privacy queries, please contact us at{" "}
        <a href="mailto:support@mdmsahayak.com" className="text-clay hover:text-clay-dark">support@mdmsahayak.com</a>.
      </p>
    </Shell>
  );
}

// =================================================================
// TERMS & CONDITIONS
// =================================================================
export function TermsConditions() {
  return (
    <Shell title="Terms &amp; Conditions" eyebrow="Legal" lastUpdated={LAST_UPDATED}>
      <p>
        These Terms &amp; Conditions ("Terms") govern your use of MDM Sahayak ("the platform",
        "the Service"). By accessing or using the platform you agree to these Terms.
        MDM Sahayak operates as an independent privately-operated software platform; it is
        not affiliated with any public administration, regulatory body, school board, or
        programme authority.
      </p>

      <h2>1. Platform Usage Rules</h2>
      <ul>
        <li>You must be at least 18 years of age (or older if so required by local law).</li>
        <li>You will not use the platform for any unlawful, fraudulent, or harmful purpose.</li>
        <li>You will not attempt to reverse-engineer, scrape, or interfere with the platform's operation.</li>
        <li>You will not impersonate any person or institution or misrepresent your identity.</li>
      </ul>

      <h2>2. User Responsibilities</h2>
      <ul>
        <li>You are responsible for the accuracy of data you enter into the platform.</li>
        <li>You are responsible for keeping your account credentials confidential.</li>
        <li>You agree to use the platform only for its intended productivity / workflow purposes.</li>
        <li>You agree to comply with all applicable laws, regulations, and institutional policies.</li>
      </ul>

      <h2>3. Service Availability</h2>
      <p>
        We strive to keep the Service available at all times but cannot guarantee uninterrupted
        access. The platform may be temporarily unavailable due to scheduled maintenance,
        upgrades, or factors outside our reasonable control. We will make commercially
        reasonable efforts to minimise such downtime.
      </p>

      <h2>4. Independent Platform Clarification</h2>
      <p>
        MDM Sahayak is an independently operated SaaS productivity tool. The platform is{" "}
        <b>not</b> an official portal, <b>not</b> an authorised public-administration
        system, and is <b>not</b> affiliated with, endorsed by, or operated by any public
        administration, regulatory body, school board, or programme authority. The platform
        provides workflow assistance tools only and helps generate properly structured
        documentation formats for productivity and convenience purposes.
      </p>
      <p>
        <b>Users should independently verify important records before official submission</b>{" "}
        and use only the appropriate official channels for any statutory submission. The
        platform does not replace official systems, official records, or any approval process
        that may be required of you under applicable laws or institutional policy.
      </p>

      <h2>5. Limitation of Liability</h2>
      <p>
        The platform is provided on an "as-is" and "as-available" basis without warranties
        of any kind, express or implied. To the maximum extent permitted by law, MDM Sahayak
        and its contributors shall not be liable for any indirect, incidental, special,
        consequential, or punitive damages — including but not limited to loss of data, loss
        of revenue, or loss of business — arising out of or related to your use of the Service.
      </p>

      <h2>6. Intellectual Property</h2>
      <p>
        All trademarks, design elements, source code, user interface elements, copy, and
        documentation in the platform are the property of their respective owners. Your
        workspace data (entries, vendor details, attendance, etc.) remains yours. You retain
        ownership of any documentation content you create using the platform.
      </p>

      <h2>7. Acceptable Use</h2>
      <p>You agree NOT to:</p>
      <ul>
        <li>Upload content that is illegal, infringing, abusive, or harmful.</li>
        <li>Attempt to disrupt, overload, or compromise platform security.</li>
        <li>Use the platform to send spam, phishing, or malicious content to other users.</li>
        <li>Share access credentials or use a single account to serve multiple unrelated organisations without our written consent.</li>
      </ul>

      <h2>8. Account Suspension Rights</h2>
      <p>
        We reserve the right to suspend or terminate an account that violates these Terms,
        engages in abusive behaviour, or causes harm to other users or the platform itself.
        Where reasonable, we will give the account holder advance notice and an opportunity
        to remedy the issue.
      </p>

      <h2>9. Modifications &amp; Updates</h2>
      <p>
        We may change these Terms from time to time. When we do, we will update the "Last
        updated" date at the top of this page. Continued use of the platform after such
        changes constitutes acceptance of the updated Terms.
      </p>

      <h2>10. Governing Law</h2>
      <p>
        These Terms are governed by the laws of India. Any disputes arising under or in
        connection with these Terms shall be subject to the exclusive jurisdiction of the
        competent courts in Bhubaneswar, Odisha.
      </p>

      <h2>11. Contact Information</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href="mailto:support@mdmsahayak.com" className="text-clay hover:text-clay-dark">support@mdmsahayak.com</a>.
      </p>
    </Shell>
  );
}

// =================================================================
// CONTACT US
// =================================================================
export function ContactUs() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Name and message are required.");
      return;
    }
    setBusy(true);
    // Compose a mailto link as a simple, dependency-free fallback. Routes
    // the user's default mail client to send the prefilled message to
    // support — keeps the contact form functional without a backend mailer.
    const body = `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`;
    const href = `mailto:support@mdmsahayak.com?subject=${encodeURIComponent(form.subject || "Contact form enquiry")}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    toast.success("Your message is ready in your email client.");
    setBusy(false);
  };
  return (
    <Shell title="Contact Us" eyebrow="Support" lastUpdated={LAST_UPDATED} showDisclaimer>
      <p>
        Need help with the platform? Our support team handles platform-related technical and
        workflow assistance enquiries. <b>Support is provided for platform-related technical
        and workflow assistance only.</b>
      </p>

      <div className="not-prose mt-8 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-clay-dark">
            <Briefcase className="h-4 w-4" />
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Business Name</p>
          </div>
          <p className="mt-2 text-sm font-semibold text-ink-900">MDM Sahayak</p>
          <p className="mt-0.5 text-xs text-ink-700">Independent SaaS Productivity Platform</p>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-clay-dark">
            <Clock className="h-4 w-4" />
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Support Timing</p>
          </div>
          <p className="mt-2 text-sm font-semibold text-ink-900">Mon – Sat · 10:00 – 18:00 IST</p>
          <p className="mt-0.5 text-xs text-ink-700">Closed on Sundays &amp; gazetted holidays.</p>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-clay-dark">
            <Mail className="h-4 w-4" />
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Email</p>
          </div>
          <a href="mailto:support@mdmsahayak.com" className="mt-2 block text-sm font-semibold text-clay hover:text-clay-dark">
            support@mdmsahayak.com
          </a>
          <p className="mt-0.5 text-xs text-ink-700">Replies typically within 1 business day.</p>
        </div>
        <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-clay-dark">
            <Phone className="h-4 w-4" />
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.18em]">Mobile / WhatsApp</p>
          </div>
          <a href="tel:+918093414633" className="mt-2 block text-sm font-semibold text-clay hover:text-clay-dark">
            +91 80934 14633
          </a>
          <a
            href="https://wa.me/918093414633?text=Hi%20MDM%20Sahayak%20support%20team%2C"
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp-btn"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-moss px-3 py-1.5 text-xs font-semibold text-white hover:bg-moss-dark"
          >
            <MessageCircle className="h-3 w-3" /> Chat on WhatsApp
          </a>
        </div>
      </div>

      <h2 className="mt-12">Send us a message</h2>
      <form onSubmit={submit} className="not-prose space-y-3 rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="contact-form">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs">
            <span className="block font-semibold text-ink-700">Your name <span className="text-rose-500">*</span></span>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="contact-name"
              className="mt-1 h-9"
              placeholder="e.g., Ramesh Kumar"
            />
          </label>
          <label className="text-xs">
            <span className="block font-semibold text-ink-700">Email (optional)</span>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email"
              className="mt-1 h-9"
              placeholder="you@example.com"
            />
          </label>
        </div>
        <label className="text-xs">
          <span className="block font-semibold text-ink-700">Subject</span>
          <Input
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            data-testid="contact-subject"
            className="mt-1 h-9"
            placeholder="What is this about?"
          />
        </label>
        <label className="text-xs">
          <span className="block font-semibold text-ink-700">Message <span className="text-rose-500">*</span></span>
          <Textarea
            required
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="contact-message"
            className="mt-1"
            placeholder="Tell us how we can help…"
          />
        </label>
        <Button type="submit" disabled={busy} data-testid="contact-submit" className="gap-1.5 bg-clay text-white hover:bg-clay-dark">
          <Send className="h-3.5 w-3.5" /> {busy ? "Preparing…" : "Send message"}
        </Button>
        <p className="mt-2 flex items-start gap-1.5 text-[0.7rem] text-ink-700">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
          The submit button opens your default email client with the message pre-filled.
          If your device has no email client configured, please email{" "}
          <a href="mailto:support@mdmsahayak.com" className="text-clay hover:text-clay-dark">support@mdmsahayak.com</a> directly.
        </p>
      </form>

      <h2 className="mt-12">Independence reminder</h2>
      <p>
        Support enquiries should pertain to platform features, technical issues, account
        access, or workflow assistance. For any matter that requires action by a public
        body, regulatory authority, school board or programme office, please contact the
        relevant office directly — MDM Sahayak is privately operated and cannot intervene
        in those processes.
      </p>
    </Shell>
  );
}

// =================================================================
// REFUND POLICY (unchanged scope from previous iterations)
// =================================================================
export function RefundPolicy() {
  return (
    <Shell title="Refund Policy" eyebrow="Legal" lastUpdated={LAST_UPDATED}>
      <p>
        This Refund Policy describes the conditions under which MDM Sahayak may issue refunds
        on paid plans and add-on services.
      </p>
      <h2>1. Eligibility</h2>
      <p>
        Refund requests may be considered for genuine, documented service failures occurring
        within <b>7 days</b> of a paid plan purchase. Requests beyond this period are generally
        not eligible.
      </p>
      <h2>2. How to request</h2>
      <p>
        Email <a href="mailto:support@mdmsahayak.com" className="text-clay hover:text-clay-dark">support@mdmsahayak.com</a>{" "}
        with your payment reference (order ID / UTR), the plan purchased, and a clear description
        of the issue. We aim to respond within 1 business day.
      </p>
      <h2>3. Non-refundable cases</h2>
      <ul>
        <li>Refund requests outside the 7-day eligibility window.</li>
        <li>Cases where the platform has been used substantially during the paid period.</li>
        <li>Cases of misuse, abuse, or violation of our Terms &amp; Conditions.</li>
      </ul>
      <h2>4. Processing</h2>
      <p>
        Approved refunds are processed via the original payment channel and typically reflect
        in your account within 7–10 working days, subject to your bank's processing time.
      </p>
      <h2>5. Contact</h2>
      <p>
        Questions? <a href="mailto:support@mdmsahayak.com" className="text-clay hover:text-clay-dark">support@mdmsahayak.com</a>.
      </p>
    </Shell>
  );
}
